import React, { useImperativeHandle, forwardRef, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import { boolean, object } from "yup";
import { useDispatch } from "react-redux";

import {
  createAlpacaUser,
  updateSignAgreement,
} from "features/SurmountAccount/service";

import { toast } from "Layout/slice";
import { Form } from "components/Form/Form";
import { contains, parseErrorMessage } from "utils/index";
import { Switch } from "components/Switch";
import { fetchUserDetails } from "Layout/slice/thunk";
import { SignAgreementContainer } from "./SignAgreement.styles.js";
import CRS_FORM from "assets/documents/CRS_QB.pdf";
import ADV_FORM from "assets/documents/ADV_QB.pdf";
import { setShowStatusModal } from "features/Dashboard/slice";
import { useNavigation } from "utils/navigate";
import { useConnectedAccounts } from "features/Dashboard/hook/useConnectedAccounts";

const initialValues = {
  agree: false,
  // crypto_agreement: true,
  margin_agreement: true,
  account_agreement: false,
  customer_agreement: false,
};

const validationSchema = object({
  agree: boolean().oneOf([true], "You must agree to proceed"),
  // crypto_agreement: boolean().required("Required"),
  margin_agreement: boolean().required("Required"),
  account_agreement: boolean().required("Required"),
  customer_agreement: boolean().required("Required"),
});

const SignAgreement = forwardRef(
  (
    { setIsSubmitting, moveToNextStep, onAgreementChange, agreementValues },
    ref
  ) => {
    const dispatch = useDispatch();
    const { navigateToDashboard } = useNavigation();
    const { refetch: refetchConnectedAccounts } = useConnectedAccounts();

    useImperativeHandle(ref, () => ({
      submitForm: () => formik.submitForm(),
    }));

    const handleSubmit = async (values) => {
      try {
        if (contains(Object.values(values), false)) {
          dispatch(
            toast({
              body: "Please accept all agreements before proceed",
            })
          );
          return;
        }
        setIsSubmitting(true);
        await updateSignAgreement(values);
        const response = await createAlpacaUser();
        dispatch(fetchUserDetails());
        refetchConnectedAccounts();
        if (moveToNextStep) {
          moveToNextStep(response.data.broker_account_id);
          return;
        }
        dispatch(setShowStatusModal("ACCOUNT_PENDING"));
        navigateToDashboard();
      } catch (error) {
        dispatch(
          toast({
            type: "error",
            body: parseErrorMessage(error),
          })
        );
        // Navigate to next step even if API fails
        if (moveToNextStep) {
          moveToNextStep();
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    const formik = useFormik({
      enableReinitialize: true,
      initialValues: agreementValues || initialValues,
      onSubmit: handleSubmit,
      validationSchema,
    });

    // Call onAgreementChange when formik values change
    useEffect(() => {
      if (onAgreementChange) {
        const relevantValues = {
          agree: formik.values.agree,
          account_agreement: formik.values.account_agreement,
          customer_agreement: formik.values.customer_agreement,
        };
        onAgreementChange(relevantValues);
      }
    }, [
      formik.values.agree,
      formik.values.account_agreement,
      formik.values.customer_agreement,
      onAgreementChange,
    ]);

    return (
      <Form>
        <SignAgreementContainer>
          <Row className="mb-3 d-none">
            <Col md={8} xs={9} className="radio">
              crypto_agreement
            </Col>
            <Col md={4} xs={3}>
              <Switch
                variant="secondary"
                size="small"
                onChange={(value) =>
                  formik.setFieldValue("crypto_agreement", value)
                }
              />
            </Col>
          </Row>

          <Row className="mb-3 d-none">
            <Col md={8} xs={9} className="radio">
              margin_agreement
            </Col>
            <Col md={4} xs={3}>
              <Switch
                variant="secondary"
                size="small"
                onChange={(value) =>
                  formik.setFieldValue("margin_agreement", value)
                }
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} className="d-flex align-items-start gap-2">
              <input
                type="checkbox"
                id="account_agreement"
                checked={formik.values.account_agreement}
                onChange={(e) =>
                  formik.setFieldValue("account_agreement", e.target.checked)
                }
                style={{
                  width: "16px",
                  height: "16px",
                  marginTop: "2px",
                  accentColor: "#2563eb",
                  cursor: "pointer",
                  flexShrink: 0,
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#f8fafc",
                  appearance: "auto",
                  WebkitAppearance: "auto",
                }}
              />
              <label
                htmlFor="account_agreement"
                style={{
                  fontSize: "14px",
                  color: "#374151",
                  lineHeight: "1.5",
                  cursor: "pointer",
                }}
              >
                I have reviewed the Quantbase Client Agreement{" "}
                <a href={CRS_FORM} target="_blank" rel="noopener noreferrer">
                  Form CRS
                </a>
                ,{" "}
                <a href={ADV_FORM} target="_blank" rel="noopener noreferrer">
                  Form ADV
                </a>
                , Part 2A Form ADV, Part 2B and understand I am signing this
                agreement electronically and that my electronic signature is the
                legal equivalent of my manual signature on the Alpaca Customer
                Agreement and Quantbase Client Agreement.
              </label>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} className="d-flex align-items-start gap-2">
              <input
                type="checkbox"
                id="customer_agreement"
                checked={formik.values.customer_agreement}
                onChange={(e) =>
                  formik.setFieldValue("customer_agreement", e.target.checked)
                }
                style={{
                  width: "16px",
                  height: "16px",
                  marginTop: "2px",
                  accentColor: "#2563eb",
                  cursor: "pointer",
                  flexShrink: 0,
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#f8fafc",
                  appearance: "auto",
                  WebkitAppearance: "auto",
                }}
              />
              <label
                htmlFor="customer_agreement"
                style={{
                  fontSize: "14px",
                  color: "#374151",
                  lineHeight: "1.5",
                  cursor: "pointer",
                }}
              >
                I have read, understood, and agree to be bound by Alpaca
                Securities LLC and Quantbase, LLC account terms, and all other
                terms, disclosures and disclaimers applicable to me, as
                referenced in the previous step. I also acknowledge that the
                Alpaca Customer Agreement contains a pre-dispute arbitration
                clause in Section 43.
              </label>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} className="d-flex align-items-start gap-2">
              <input
                type="checkbox"
                id="agree"
                checked={formik.values.agree}
                onChange={(e) =>
                  formik.setFieldValue("agree", e.target.checked)
                }
                onBlur={() => formik.setFieldTouched("agree", true)}
                style={{
                  width: "16px",
                  height: "16px",
                  marginTop: "2px",
                  accentColor: "#2563eb",
                  cursor: "pointer",
                  flexShrink: 0,
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#f8fafc",
                  appearance: "auto",
                  WebkitAppearance: "auto",
                }}
              />
              <label
                htmlFor="agree"
                style={{
                  fontSize: "14px",
                  color: "#374151",
                  lineHeight: "1.5",
                  cursor: "pointer",
                }}
              >
                By clicking I agree I understand I am signing this agreement
                electronically, and that my electronic signature will have the
                same effect as physically signing and returning the Application
                Agreement.
              </label>
            </Col>
          </Row>
        </SignAgreementContainer>
      </Form>
    );
  }
);

SignAgreement.displayName = "SignAgreement";

SignAgreement.propTypes = {
  setIsSubmitting: PropTypes.func,
  moveToNextStep: PropTypes.func,
  onAgreementChange: PropTypes.func,
  agreementValues: PropTypes.object,
};

export { SignAgreement };
